import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { ReactComponent as IconDoubleArrowRight } from '../../assets/icon/ic_double_arrow_right.svg';
import { ReactComponent as IconDoubleArrowLeft } from '../../assets/icon/ic_double_arrow_left.svg';
import { setApplicationState } from '../../actions/application/application';
import sideBarContent from '../../common/sideBarSettings';
import { IconLogo } from '../../assets/icon';
import NavMenuItem from './NavMenuItem';

const SideBar = ({ mobileOpen, onMobileClose }) => {
  const dispatch = useDispatch();
  const isAdmin = useSelector((state) => state.getMyProfile?.permissions?.some(
    ({ group, permission }) => group === 'admin' && permission === 'admin'
  ));
  const [theme, setTheme] = useState(() => {
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme) {
      return storedTheme;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const [themeMode, setThemeMode] = useState(() => localStorage.getItem('themeMode') || 'system');
  const sidebarRef = useRef(null);

  const {
    application: {
      sidebarVisibility,
      sidebarActiveMenu: {
        id: activeMenuId,
        visible
      } = {},
    }
  } = useSelector((state) => ({
    application: state.application
  }), shallowEqual);
  const isCollapsed = !sidebarVisibility;
  const sidebarWidthClassName = isCollapsed ? 'md:w-[72px]' : 'md:w-[260px]';
  const headerPaddingClassName = isCollapsed ? 'px-[8px] justify-center' : 'px-[20px]';

  const onClickMenu = () => {
    localStorage.setItem('sidebarVisibility', `${!sidebarVisibility}`);
    dispatch(setApplicationState({ sidebarVisibility: !sidebarVisibility }));
  };

  const onClickSideBarMenuItem = ({ id, path }) => {
    dispatch(setApplicationState(
      { sidebarActiveMenu: { id, visible: path ? false : !visible } }
    ));
    if (path) onMobileClose();
  };

  useEffect(() => {
    const isDark = theme === 'dark';
    document.documentElement.classList.toggle('dark', isDark);
    if (themeMode === 'system') {
      localStorage.removeItem('theme');
      localStorage.setItem('themeMode', 'system');
    } else {
      localStorage.setItem('theme', theme);
      localStorage.setItem('themeMode', themeMode);
    }
  }, [theme, themeMode]);

  useEffect(() => {
    if (themeMode !== 'system') {
      return undefined;
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event) => setTheme(event.matches ? 'dark' : 'light');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [themeMode]);

  useEffect(() => {
    if (themeMode === 'system') {
      setTheme(window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
      return;
    }
    setTheme(themeMode);
  }, [themeMode]);

  useEffect(() => {
    const onDocumentClick = (event) => {
      if (!visible) {
        return;
      }
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        dispatch(setApplicationState({ sidebarActiveMenu: { id: activeMenuId, visible: false } }));
      }
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, [visible, activeMenuId, dispatch]);

  const footerClassName = [
    'border-t border-dashed border-[#e5e7eb] dark:border-[#1b263b]',
    'px-[12px] py-[12px] flex flex-col gap-[8px]'
  ].join(' ');
  const themeLabelClassName = [
    'text-[11px] uppercase tracking-[0.6px] text-[#94a3b8] dark:text-[#778da9] px-[6px]'
  ].join(' ');
  const isDarkTheme = theme === 'dark';

  const sliderPositionMap = {
    light: 'translate-x-[3px]',
    system: 'translate-x-[24px]',
    dark: 'translate-x-[45px]'
  };
  const sliderPositionClassName = sliderPositionMap[themeMode] || sliderPositionMap.system;
  const sliderCollapsedPositionMap = {
    light: 'translate-x-[3px]',
    system: 'translate-x-[12px]',
    dark: 'translate-x-[21px]'
  };
  const sliderCollapsedPositionClassName = sliderCollapsedPositionMap[themeMode]
    || sliderCollapsedPositionMap.system;
  const themeIconClassName = `w-[14px] h-[14px] ${isDarkTheme ? 'text-[#f8c14d]' : 'text-[#f59e0b]'}`;
  const SunIcon = () => (
    <svg className={themeIconClassName} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="4.5" stroke="currentColor" strokeWidth="2" />
      <path
        d="M12 2.5v2.5M12 19v2.5M2.5 12h2.5M19 12h2.5
        M4.6 4.6l1.8 1.8M17.6 17.6l1.8 1.8M19.4 4.6l-1.8 1.8M6.4 17.6l-1.8 1.8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
  const MoonIcon = () => (
    <svg className={themeIconClassName} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M21 14.5a8.5 8.5 0 1 1-11.5-11 7 7 0 0 0 11.5 11Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    <div
      ref={sidebarRef}
      className={`fixed top-0 bottom-0 left-0 z-[98] flex flex-col md:block transition-all duration-300 ease-out
      bg-white dark:bg-[#0d1b2a] border-r border-[#e5e7eb] dark:border-[#1b263b]
      shadow-[6px_0_18px_rgba(15,23,42,0.08)] dark:shadow-[8px_0_24px_rgba(0,0,0,0.45)]
      w-[280px] ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
      ${sidebarWidthClassName}`}
      role="presentation"
    >
      <div
        className="flex md:hidden flex-row items-center justify-between px-[16px]
        bg-white dark:bg-[#0d1b2a] h-[56px] border-b border-dashed
        border-[#e5e7eb] dark:border-[#1b263b]"
      >
        <Link
          to="/"
          className="flex items-center text-[20px] text-info font-['Fredoka_One',cursive]"
          onClick={onMobileClose}
        >
          <IconLogo height={34} />
        </Link>
        <button
          type="button"
          aria-label="Close navigation"
          className="w-[40px] h-[40px] flex items-center justify-center rounded-[8px]
          text-[#64748b] hover:bg-[#f1f5f9] dark:text-[#94a3b8] dark:hover:bg-[#1b263b]"
          onClick={onMobileClose}
        >
          <svg viewBox="0 0 24 24" className="w-[22px] h-[22px]" fill="none" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <div
        className={`hidden md:flex flex-row items-center bg-white dark:bg-[#0d1b2a] h-[65px]
        transition-all duration-300 ease-out border-b border-dashed border-[#e5e7eb] dark:border-[#1b263b]
        ${headerPaddingClassName}`}
      >
        <div className={`flex items-center ${isCollapsed ? 'justify-center w-full' : 'justify-between w-full'}`}>
          {!isCollapsed && (
            <Link
              to="/"
              className="flex items-center text-[20px] text-info font-['Fredoka_One',cursive]"
            >
              <IconLogo height={36} />
            </Link>
          )}
          <div className="cursor-pointer" onClick={onClickMenu} role="presentation">
            {sidebarVisibility
              ? (
                <IconDoubleArrowLeft
                  className="text-[#94a3b8] dark:text-[#778da9] transition-all duration-300 ease-out hover:text-info"
                  width={28}
                  height={28}
                />
              )
              : (
                <IconDoubleArrowRight
                  className="text-[#94a3b8] dark:text-[#778da9] transition-all duration-300 ease-out hover:text-info"
                  width={28}
                  height={28}
                />
              )}
          </div>
        </div>
      </div>
      <div className="flex-1 my-[12px] overflow-y-auto md:overflow-visible customScroll">
        <div>
          {sideBarContent.filter((menuItem) => !menuItem.adminOnly || isAdmin).map((menuItem) => (
            <div
              className={`transition-all duration-300 ease-out hover:bg-[#f8fafc] dark:hover:bg-transparent
              ${isCollapsed ? 'relative' : ''}`}
              key={`menu_${menuItem?.id}`}
              role="presentation"
            >
              <NavMenuItem
                isOpen={(menuItem?.id === activeMenuId && visible)}
                item={menuItem}
                onClick={() => onClickSideBarMenuItem(menuItem)}
                isActive={menuItem?.id === activeMenuId}
                isCollapsed={!sidebarVisibility}
              />
              {menuItem?.list && (() => {
                const collapsedMenuClassName = [
                  'md:absolute md:left-full md:top-0 md:ml-[8px] md:min-w-[220px] md:rounded-[8px]',
                  'md:border md:border-[#e5e7eb] dark:md:border-[#1b263b]',
                  'md:bg-white dark:md:bg-[#1b263b] md:shadow-[0_10px_24px_rgba(15,23,42,0.12)]',
                  'py-[6px] md:z-[20]'
                ].join(' ');
                return (
                  <div
                    className={`${menuItem?.id === activeMenuId && visible ? 'block' : 'hidden'} ${
                      isCollapsed ? collapsedMenuClassName : ''
                    }`}
                  >
                    {menuItem?.list.filter((submenuItem) => !submenuItem.adminOnly || isAdmin).map((submenuItem) => (
                      <NavMenuItem
                        submenu
                        item={submenuItem}
                        key={submenuItem.id}
                        isCollapsed={!sidebarVisibility}
                        showLabel={isCollapsed}
                        onClick={onMobileClose}
                      />
                    ))}
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      </div>
      <div className={footerClassName}>
        <div className={`${themeLabelClassName} ${sidebarVisibility ? '' : 'md:hidden'}`}>
          Theme
        </div>
        <div className={`flex flex-col gap-[10px] ${isCollapsed ? 'items-center' : ''}`}>
          <button
            type="button"
            onClick={() => {
              const nextModeMap = {
                light: 'system',
                system: 'dark',
                dark: 'light'
              };
              const nextMode = nextModeMap[themeMode] || 'system';
              setThemeMode(nextMode);
            }}
            className={`relative h-[24px] rounded-full transition-colors duration-300
            bg-[#f1f5f9] dark:bg-[#1b263b]
            ${isCollapsed ? 'w-[42px]' : 'w-[66px]'}`}
          >
            <div
              className={`absolute top-[3px] w-[18px] h-[18px] rounded-full bg-white
              flex items-center justify-center shadow-sm transition-transform duration-300
              ${isCollapsed ? sliderCollapsedPositionClassName : sliderPositionClassName}`}
            >
              {themeMode === 'dark' && <MoonIcon />}
              {themeMode === 'light' && <SunIcon />}
              {themeMode === 'system' && (
                <div className={`text-[10px] font-bold ${isDarkTheme ? 'text-[#f8c14d]' : 'text-[#f59e0b]'}`}>
                  A
                </div>
              )}
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SideBar;

SideBar.propTypes = {
  mobileOpen: PropTypes.bool,
  onMobileClose: PropTypes.func,
};

SideBar.defaultProps = {
  mobileOpen: false,
  onMobileClose: () => {},
};
